/**
 * Lifecycle of the non-video data channels the server pushes to us (telemetry, chat/
 * "bot" replies): open a `DataConsumer` for each `newDataConsumer` message, route its
 * incoming messages by `label`, keep track of the single "current" telemetry consumer
 * (closing an older one when a newer telemetry producer appears), and close everything
 * on reset. Fully self-contained — it does not share any state with the video-consume
 * side in `room-consumers.ts`, only the same `appendDiag`/`protoo`-shaped collaborators.
 */

import type { types as Mediasoup } from "mediasoup-client";

import type { NewDataConsumerData } from "./protoo-messages";

export interface CreateDataConsumerRegistryDeps {
  appendDiag: (s: string, opts?: { level?: string }) => void;
  appendSocketIoDcMessage: (m: unknown) => void;
  onTelemetryOpen?: () => void;
  onTelemetryClose?: () => void;
}

export function createDataConsumerRegistry({
  appendDiag,
  appendSocketIoDcMessage,
  onTelemetryOpen,
  onTelemetryClose,
}: CreateDataConsumerRegistryDeps) {
  const dataConsumers: Mediasoup.DataConsumer[] = [];
  const consumedDataProducers = new Set<string>();
  let telemetryDataConsumer: Mediasoup.DataConsumer | null = null;

  function removeDataConsumer(dc: Mediasoup.DataConsumer) {
    const idx = dataConsumers.indexOf(dc);
    if (idx >= 0) dataConsumers.splice(idx, 1);
  }

  function closeTelemetryDataConsumer(reason: string) {
    if (!telemetryDataConsumer) return;
    appendDiag(`telemetry: close previous consumer (${reason})`);
    try {
      telemetryDataConsumer.close();
    } catch {
      /* ignore */
    }
    removeDataConsumer(telemetryDataConsumer);
    telemetryDataConsumer = null;
  }

  function routeDataConsumerMessage(label: string, message: unknown) {
    const normalized = (label || "").toLowerCase();
    // `telemetry` (unreliable stream) and `events` (reliable: robot-config, errors) are
    // both robot → operator business data — PROTOCOL.md §2/§4.
    if (normalized === "telemetry" || normalized === "events") {
      appendSocketIoDcMessage(message);
      return;
    }
    // Other operators' XR frames arrive on `control` at ~30 Hz. Rendering their
    // full pose payloads in diagnostics can overwhelm the video/model tab.
    if (normalized === "control") return;
    // Every monitor tab broadcasts this at 10 Hz. The echo is routine state,
    // and filling the diagnostics panel with it burdens the three-video view.
    if (normalized === "chat" && typeof message === "string"
        && /"event"\s*:\s*"monitor-heartbeat"/.test(message)) return;
    if (normalized === "bot") {
      appendDiag(`[bot] ${message}`, { level: "debug" });
      return;
    }
    appendDiag(`[${label || "?"}] ${message}`, { level: "debug" });
  }

  /**
   * Open a `DataConsumer` for one `newDataConsumer` message. A duplicate
   * `dataProducerId` (the server retried a message we already handled) is a silent
   * no-op, not an error — the caller ACKs either way. Throws on a malformed payload or
   * if `recvTransport.consumeData` itself rejects; the caller (`room-consumers.ts`)
   * lets that propagate so the session can surface a real setup failure.
   */
  async function handleNewDataConsumer(
    recvTransport: Mediasoup.Transport,
    d: NewDataConsumerData
  ): Promise<void> {
    const dataConsumerId = d.id || d.dataConsumerId;
    if (!dataConsumerId || !d.dataProducerId || !d.sctpStreamParameters) {
      throw new Error(`Invalid newDataConsumer: ${JSON.stringify(d)}`);
    }
    const label = d.label || "bot";
    const normalizedLabel = label.toLowerCase();
    if (consumedDataProducers.has(d.dataProducerId)) {
      appendDiag(`skip duplicate dataConsumer producer=${d.dataProducerId} label=${label}`);
      return;
    }
    if (normalizedLabel === "telemetry") {
      closeTelemetryDataConsumer("newer telemetry producer in room");
    }
    appendDiag(`newDataConsumer label=${label} id=${dataConsumerId}`);
    const dataConsumer = await recvTransport.consumeData({
      id: dataConsumerId,
      dataProducerId: d.dataProducerId,
      sctpStreamParameters: d.sctpStreamParameters,
      label,
      protocol: d.protocol || "",
      appData: d.appData || {},
    });
    dataConsumer.on("open", () => {
      appendDiag(`dataChannel open [${label}]`);
      if (normalizedLabel === "telemetry") onTelemetryOpen?.();
    });
    dataConsumer.on("message", (m) => routeDataConsumerMessage(label, m));
    dataConsumer.on("error", (e) =>
      appendDiag(`data consumer error label=${label} id=${dataConsumer.id}: ${e}`, {
        level: "warn",
      })
    );
    dataConsumer.on("close", () => {
      appendDiag(
        `data consumer closed label=${label} id=${dataConsumer.id} (SCTP Close — usually remote peer/ICE, not protoo WS)`,
        { level: "warn" }
      );
      if (telemetryDataConsumer === dataConsumer) {
        telemetryDataConsumer = null;
        onTelemetryClose?.();
      }
    });
    dataConsumers.push(dataConsumer);
    consumedDataProducers.add(d.dataProducerId);
    if (normalizedLabel === "telemetry") {
      telemetryDataConsumer = dataConsumer;
    }
  }

  function closeDataConsumers() {
    onTelemetryClose?.();
    for (const dc of dataConsumers) {
      try {
        dc.close();
      } catch {
        /* ignore */
      }
    }
    dataConsumers.length = 0;
    telemetryDataConsumer = null;
    consumedDataProducers.clear();
  }

  return { handleNewDataConsumer, closeDataConsumers };
}

export type DataConsumerRegistry = ReturnType<typeof createDataConsumerRegistry>;
